import { useState } from "react";

import { IClinicianForm } from "../../../@types/admin/AdminSurvey";
import { useFileDownload } from "../../../hooks/mutations/admin/fileTransfer";
import { surveyExportRequests } from "../../../services/admin/surveysService";
import {
  AdminDateField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../shared";
import { ModalBodyText } from "./AdminSurveys.styles";

interface IClinicianFormReportModalProps {
  form: IClinicianForm;
  onClose: () => void;
}

/**
 * Generates a clinician form's report over a date range — Retool's
 * `exportModal` plus its `ExportClinicianForm` query (EN-5164).
 *
 * The dates are optional (`SurveyReportRequesDto` takes them nullable), so an
 * empty range means "everything". Retool sent
 * `moment(undefined).utc().toISOString()` for an unset date, which is *now*,
 * not null — an unset "from" silently became today and the report came back
 * empty. Blank fields are sent as null here.
 */
export function ClinicianFormReportModal({
  form,
  onClose,
}: IClinicianFormReportModalProps) {
  const { downloadFile, isDownloading } = useFileDownload();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const rangeError =
    fromDate && toDate && fromDate > toDate
      ? "The start date must be on or before the end date."
      : undefined;

  const handleExport = async () => {
    if (!form.id || rangeError) return;

    const saved = await downloadFile(
      surveyExportRequests.clinicianFormReport(
        form.id,
        fromDate ? new Date(fromDate).toISOString() : null,
        // The filter is AnsweredDate <= toDate; a bare date parses as
        // midnight UTC, which silently drops everything answered ON the
        // selected end day, so it is sent as that day's last instant.
        toDate ? new Date(`${toDate}T23:59:59.999Z`).toISOString() : null
      ),
      {
        fallbackFilename: `${form.name ?? "Survey"}-Report.xlsx`,
        errorMessage: "The report could not be generated. Try again.",
      }
    );

    if (saved) onClose();
  };

  return (
    <ModalContainer>
      <ModalTitle>Generate report</ModalTitle>

      <ModalForm>
        <ModalBodyText>
          Builds the report for <strong>{form.name ?? "this form"}</strong>.
        </ModalBodyText>

        <FilterGroup>
          <FilterLabel htmlFor="report-from">From</FilterLabel>
          <AdminDateField
            id="report-from"
            value={fromDate}
            disabled={isDownloading}
            onChange={setFromDate}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="report-to">To</FilterLabel>
          <AdminDateField
            id="report-to"
            value={toDate}
            disabled={isDownloading}
            onChange={setToDate}
          />
          {rangeError && <FieldError>{rangeError}</FieldError>}
          <FieldHint>Leave both blank to report on every response.</FieldHint>
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isDownloading}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isDownloading || !form.id || Boolean(rangeError)}
            onClick={handleExport}
          >
            {isDownloading ? "Generating..." : "Generate report"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
